/* ============================================================================
   RELOURA — OUR STORY  (Shopify asset: our-story.js)
   Animation layer: GSAP + ScrollTrigger, vanilla JS.

   This is the same choreography as the standalone build, restructured into
   an init()/teardown() pair so it is safe to run inside the Shopify Theme
   Editor, where the section's HTML can be replaced via AJAX every time a
   merchant changes a setting (uploads an image, etc.) without a full page
   reload. Every ScrollTrigger this file creates is tracked, so a re-init
   always kills the previous run's triggers/pins before building new ones —
   nothing stacks up or duplicates.

   Structure:
   1.  Boot / lifecycle
   2.  Reduced-motion guard
   3.  Global scroll-progress "wick" thread
   4.  Reusable reveal helpers
   5.  Section 1 — Hero intro timeline
   6.  Section 2 — "Nothing" pinned sequence
   7.  Section 3 — Two friends
   8.  Section 4 — Early days
   9.  Section 5 — The reality pinned sequence
   10. Section 6 — Support
   11. Section 7 — Birth of Reloura
   12. Section 8 — Why candles
   13. Section 9 — Memories pinned sequence
   14. Section 10 — Essence words
   15. Section 11 — Vision
   16. Section 12 — Ending
   17. Resize handling + refresh after images load
   ============================================================================ */

(function () {
  "use strict";

  var STORY_SELECTOR = "[data-our-story]";
  var triggers = [];
  var initialized = false;

  // Tracks the ScrollTrigger attached to a tween/timeline.
  function tracked(animation) {
    if (animation && animation.scrollTrigger) {
      triggers.push(animation.scrollTrigger);
    }
    return animation;
  }

  /* ==========================================================================
     1. TEARDOWN
     ========================================================================== */

  function teardown() {
    triggers.forEach(function (st) {
      if (st && typeof st.kill === "function") {
        st.kill();
      }
    });

    triggers = [];

    if (typeof gsap !== "undefined") {
      gsap.set(
        [
          ".fade-up",
          ".reveal-lines .line",
          ".hero__headline .line",
          ".hero__kicker",
          ".hero__support",
          ".hero__scroll-cue",
          ".hero__inner",
          ".nothing-list__item",
          ".reality-beat",
          ".reality-beat__climax",
          ".memory-scene",
          ".birth__wordmark",
          ".birth__glow",
          "#wickFill"
        ].join(", "),
        {
          clearProps: "all"
        }
      );
    }

    initialized = false;
  }

  /* ==========================================================================
     BOOT
     ========================================================================== */

  function boot() {
    if (!document.querySelector(STORY_SELECTOR)) return;

    if (
      typeof gsap === "undefined" ||
      typeof ScrollTrigger === "undefined"
    ) {
      return;
    }

    teardown();
    init();
  }

  function init() {
    if (initialized) return;

    initialized = true;

    gsap.registerPlugin(ScrollTrigger);

    /* ------------------------------------------------------------------
       Mobile Safari/Chrome resize handling
       ------------------------------------------------------------------ */

    ScrollTrigger.config({
      ignoreMobileResize: true
    });

    var isTouch =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0;

    if (isTouch) {
      ScrollTrigger.normalizeScroll(true);
    }

    var root = document.querySelector(STORY_SELECTOR);

    /* ------------------------------------------------------------------
       2. REDUCED MOTION
       ------------------------------------------------------------------ */

    /* ------------------------------------------------------------------
       3. GLOBAL SCROLL-PROGRESS "WICK" THREAD
       ------------------------------------------------------------------ */

    tracked(
      gsap.to("#wickFill", {
        height: "100%",
        ease: "none",
        scrollTrigger: {
          trigger: "main",
          start: "top top",
          end: "bottom bottom",
          scrub: 0.3
        }
      })
    );

    /* ------------------------------------------------------------------
       4. REUSABLE REVEAL HELPERS
       ------------------------------------------------------------------ */

    function revealLines(selector, opts) {
      opts = opts || {};

      document.querySelectorAll(selector).forEach(function (el) {
        var lines = el.querySelectorAll(".line");

        if (!lines.length) return;

        gsap.set(lines, {
          yPercent: 110
        });

        tracked(
          gsap.to(lines, {
            yPercent: 0,
            duration: 0.9,
            ease: "power3.out",
            stagger: 0.08,
            scrollTrigger: {
              trigger: el,
              start: opts.start || "top 80%",
              toggleActions: "play none none reverse"
            }
          })
        );
      });
    }

    function fadeUps(scopeSelector) {
      var scope = scopeSelector
        ? document.querySelector(scopeSelector)
        : document;

      if (!scope) return;

      scope.querySelectorAll(".fade-up").forEach(function (el) {
        tracked(
          gsap.to(el, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              toggleActions: "play none none reverse"
            }
          })
        );
      });
    }

    /* ------------------------------------------------------------------
       5. SECTION 1 — HERO
       ------------------------------------------------------------------ */

    (function heroIntro() {
      var lines = document.querySelectorAll(
        ".hero__headline .line"
      );

      gsap.set(lines, {
        yPercent: 110
      });

      gsap.set(".hero__kicker", {
        opacity: 0
      });

      gsap.set(".hero__support", {
        opacity: 0,
        y: 16
      });

      gsap.set(".hero__scroll-cue", {
        opacity: 0
      });

      gsap
        .timeline({
          delay: 0.2
        })
        .to(lines, {
          yPercent: 0,
          duration: 1.1,
          ease: "power3.out",
          stagger: 0.12
        })
        .to(
          ".hero__kicker",
          {
            opacity: 1,
            duration: 0.6,
            ease: "power1.out"
          },
          0
        )
        .fromTo(
          ".hero__kicker",
          {
            y: 10
          },
          {
            y: 0,
            duration: 0.6,
            ease: "power1.out"
          },
          0
        )
        .to(
          ".hero__support",
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out"
          },
          "-=0.5"
        )
        .to(
          ".hero__scroll-cue",
          {
            opacity: 1,
            duration: 0.6,
            ease: "power1.out"
          },
          "-=0.4"
        );

      tracked(
        gsap.to(".hero__inner", {
          opacity: 0,
          y: -40,
          ease: "none",
          scrollTrigger: {
            trigger: "#section-hero",
            start: "top top",
            end: "bottom top",
            scrub: true
          }
        })
      );
    })();

    /* ------------------------------------------------------------------
       6. SECTION 2 — "NOTHING"
       ------------------------------------------------------------------ */

    function buildNothingSequence(scrollFactor) {
      var items = gsap.utils.toArray(
        ".nothing-list__item"
      );

      if (!items.length) return;

      gsap.set(items, {
        opacity: 0,
        y: 24
      });

      gsap.set(items[0], {
        opacity: 1,
        y: 0
      });

      var tl = tracked(
        gsap.timeline({
          scrollTrigger: {
            trigger: "#section-nothing",
            start: "top top",
            end:
              "+=" +
              Math.round(
                window.innerHeight *
                  scrollFactor *
                  items.length
              ),
            scrub: 0.6,
            pin: true,
            anticipatePin: 1
          }
        })
      );

      items.forEach(function (item, i) {
        if (i === 0) return;

        tl.to(
          items[i - 1],
          {
            opacity: 0,
            y: -24,
            duration: 0.4
          },
          i
        ).fromTo(
          item,
          {
            opacity: 0,
            y: 24
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.4
          },
          i
        );
      });

      tl.to({}, {
        duration: 0.6
      });
    }

    ScrollTrigger.matchMedia({
      "(min-width: 601px)": function () {
        buildNothingSequence(1.2);
      },

      "(max-width: 600px)": function () {
        buildNothingSequence(0.55);
      }
    });

    /* ------------------------------------------------------------------
       7. SECTION 3 — TWO FRIENDS
       ------------------------------------------------------------------ */

    revealLines(".two-friends__text h2");
    fadeUps("#section-two-friends");

    /* ------------------------------------------------------------------
       8. SECTION 4 — EARLY DAYS
       ------------------------------------------------------------------ */

    fadeUps("#section-early-days");

    /* ------------------------------------------------------------------
       9. SECTION 5 — THE REALITY
       ------------------------------------------------------------------ */

    function buildRealitySequence(scrollFactor) {
      var beats = gsap.utils.toArray(
        ".reality-beat"
      );

      if (!beats.length) return;

      gsap.set(beats, {
        opacity: 0,
        y: 20
      });

      gsap.set(beats[0], {
        opacity: 1,
        y: 0
      });

      var climax = beats[beats.length - 1];

      gsap.set(
        climax.querySelector(
          ".reality-beat__climax"
        ),
        {
          scale: 0.9
        }
      );

      var tl = tracked(
        gsap.timeline({
          scrollTrigger: {
            trigger: "#section-reality",
            start: "top top",
            end:
              "+=" +
              Math.round(
                window.innerHeight *
                  scrollFactor *
                  beats.length
              ),
            scrub: 0.6,
            pin: true,
            anticipatePin: 1
          }
        })
      );

      beats.forEach(function (beat, i) {
        if (i === 0) return;

        tl.to(
          beats[i - 1],
          {
            opacity: 0,
            y: -20,
            duration: 0.4
          },
          i
        ).fromTo(
          beat,
          {
            opacity: 0,
            y: 20
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.4
          },
          i
        );
      });

      tl.to(
        climax.querySelector(
          ".reality-beat__climax"
        ),
        {
          scale: 1,
          duration: 0.6,
          ease: "power2.out"
        },
        beats.length - 0.6
      );

      tl.to({}, {
        duration: 0.7
      });
    }

    ScrollTrigger.matchMedia({
      "(min-width: 601px)": function () {
        buildRealitySequence(1.1);
      },

      "(max-width: 600px)": function () {
        buildRealitySequence(0.6);
      }
    });

    /* ------------------------------------------------------------------
       10. SECTION 6 — SUPPORT
       ------------------------------------------------------------------ */

    fadeUps("#section-support");

    /* ------------------------------------------------------------------
       11. SECTION 7 — BIRTH OF RELOURA
       ------------------------------------------------------------------ */

    revealLines(".birth__pre");
    fadeUps("#section-birth");

    tracked(
      gsap.fromTo(
        ".birth__wordmark",
        {
          opacity: 0,
          scale: 0.92
        },
        {
          opacity: 1,
          scale: 1,
          duration: 1.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: "#section-birth",
            start: "top 60%",
            toggleActions: "play none none reverse"
          }
        }
      )
    );

    tracked(
      gsap.fromTo(
        ".birth__glow",
        {
          opacity: 0,
          scale: 0.8
        },
        {
          opacity: 1,
          scale: 1.1,
          duration: 1.6,
          ease: "power1.out",
          scrollTrigger: {
            trigger: "#section-birth",
            start: "top 70%",
            toggleActions: "play none none reverse"
          }
        }
      )
    );

    /* ------------------------------------------------------------------
       12. SECTION 8 — WHY CANDLES
       ------------------------------------------------------------------ */

    revealLines(".why-candles__statement");
    fadeUps("#section-why-candles");

    /* ------------------------------------------------------------------
       13. SECTION 9 — MEMORIES
       ------------------------------------------------------------------ */

    function buildMemoriesSequence(scrollFactor) {
      var scenes = gsap.utils.toArray(
        ".memory-scene"
      );

      var dots = gsap.utils.toArray(
        ".memories-section__dot"
      );

      if (!scenes.length) return;

      gsap.set(scenes, {
        opacity: 0,
        y: 16
      });

      gsap.set(scenes[0], {
        opacity: 1,
        y: 0
      });

      var tl = tracked(
        gsap.timeline({
          scrollTrigger: {
            trigger: "#section-memories",
            start: "top top",
            end:
              "+=" +
              Math.round(
                window.innerHeight *
                  scrollFactor *
                  scenes.length
              ),
            scrub: 0.6,
            pin: true,
            anticipatePin: 1,

            onUpdate: function (self) {
              var idx = Math.min(
                scenes.length - 1,
                Math.floor(
                  self.progress *
                    scenes.length
                )
              );

              dots.forEach(function (dot, i) {
                dot.classList.toggle(
                  "is-active",
                  i === idx
                );
              });
            }
          }
        })
      );

      scenes.forEach(function (scene, i) {
        if (i === 0) return;

        tl.to(
          scenes[i - 1],
          {
            opacity: 0,
            y: -16,
            duration: 0.4
          },
          i
        ).fromTo(
          scene,
          {
            opacity: 0,
            y: 16
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.4
          },
          i
        );
      });

      tl.to({}, {
        duration: 0.6
      });
    }

    ScrollTrigger.matchMedia({
      "(min-width: 601px)": function () {
        buildMemoriesSequence(1.3);
      },

      "(max-width: 600px)": function () {
        buildMemoriesSequence(0.7);
      }
    });

    /* ------------------------------------------------------------------
       14. SECTION 10 — ESSENCE WORDS
       ------------------------------------------------------------------ */

    gsap.utils
      .toArray(".essence-word")
      .forEach(function (word, i) {
        tracked(
          gsap.to(word, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power2.out",
            delay: i * 0.12,
            scrollTrigger: {
              trigger: ".essence-words",
              start: "top 75%",
              toggleActions: "play none none reverse"
            }
          })
        );
      });

    fadeUps("#section-essence");

    /* ------------------------------------------------------------------
       15. SECTION 11 — VISION
       ------------------------------------------------------------------ */

    revealLines(".vision-section__lede");

    revealLines(
      ".vision-section__resolve",
      {
        start: "top 78%"
      }
    );

    fadeUps("#section-vision");

    /* ------------------------------------------------------------------
       16. SECTION 12 — ENDING
       ------------------------------------------------------------------ */

    revealLines(".ending-section__statement");
    fadeUps("#section-ending");

    /* ------------------------------------------------------------------
       17. REFRESH AFTER IMAGES LOAD
       ------------------------------------------------------------------ */

    if (root) {
      root
        .querySelectorAll(
          "img.image-placeholder__img"
        )
        .forEach(function (img) {
          if (img.complete) return;

          img.addEventListener(
            "load",
            function () {
              ScrollTrigger.refresh();
            },
            {
              once: true
            }
          );
        });
    }

    ScrollTrigger.refresh();
  }

  /* ==========================================================================
     RESIZE HANDLING
     ========================================================================== */

  var resizeTimer;

  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(function () {
      if (
        typeof ScrollTrigger !== "undefined"
      ) {
        ScrollTrigger.refresh();
      }
    }, 250);
  });

  /* ==========================================================================
     SHOPIFY THEME EDITOR LIFECYCLE
     ========================================================================== */

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      boot
    );
  } else {
    boot();
  }

  document.addEventListener(
    "shopify:section:load",
    boot
  );

  document.addEventListener(
    "shopify:section:unload",
    teardown
  );
})();